import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../../api/api";
import { toast } from "react-toastify";

export const AddProduct = () => {
  const navigate = useNavigate();

  // Form state
  const [name, setName] = useState("");
  const [category, setCategory] = useState(""); // Starts empty - important!
  const [desc, setDesc] = useState("");
  const [price, setPrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [stockQty, setStockQty] = useState("");
  const [weight, setWeight] = useState("");
  const [visibility, setVisibility] = useState("local");
  const [productType, setProductType] = useState("");
  const [sellerName, setSellerName] = useState("");
  const [sellerPhone, setSellerPhone] = useState("");

  // Categories & Store
  const [categories, setCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingStoreInfo, setLoadingStoreInfo] = useState(true);
  const [storeName, setStoreName] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showStorePopup, setShowStorePopup] = useState(false);

  // Media state
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);

  // Fetch Store Info
  const handleFetchStore = async () => {
    try {
      setLoadingStoreInfo(true);
      const res = await api.business.getStore();

      if (res?.shop) {
        const shop = res.shop;
        setStoreName(shop.storeName || "");
        setSellerPhone(shop.phoneNumber || "");
      }
    } catch (error) {
      console.log("Error fetching store:", error);
    } finally {
      setLoadingStoreInfo(false);
    }
  };

  useEffect(() => {
    handleFetchStore();
  }, []);

  // Show store setup popup
  useEffect(() => {
    const hasStore = localStorage.getItem("hasStore");
    if (!hasStore) {
      setShowStorePopup(true);
    }
  }, []);

  // Fetch Categories
  useEffect(() => {
    (async () => {
      try {
        setLoadingCats(true);
        const cats = await api.categories.list();
        setCategories(Array.isArray(cats) ? cats : []);
      } catch (err) {
        console.error("Failed to load categories:", err);
        toast.error("Failed to load categories. Please try again.");
      } finally {
        setLoadingCats(false);
      }
    })();
  }, []);

  const onPick = () => inputRef.current?.click();

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;

    const remaining = 5 - files.length;
    const toAdd = list.slice(0, remaining).map((f, idx) => ({
      id: `${Date.now()}_${idx}`,
      name: f.name,
      sizeKB: Math.max(1, Math.round(f.size / 1024)),
      src: URL.createObjectURL(f),
      progress: 0,
      file: f,
    }));

    setFiles((prev) => [...prev, ...toAdd].slice(0, 5));

    // Simulate upload progress
    toAdd.forEach((fileObj, i) => {
      setTimeout(() => {
        setFiles((prev) =>
          prev.map((it) =>
            it.id === fileObj.id ? { ...it, progress: 100 } : it
          )
        );
      }, 800 + i * 300);
    });

    e.target.value = "";
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // Updated validation - requires a real category to be selected
  const canPublish = useMemo(() => {
    const realFiles = files.filter((f) => !!f.file);

    return (
      name.trim().length > 2 &&
      category.length > 0 &&                    // Must be a valid category _id
      (visibility === "local" || visibility === "global") &&
      String(price).trim() &&
      String(stockQty).trim() &&
      realFiles.length >= 1
    );
  }, [name, category, visibility, price, stockQty, files]);

  const publish = async (e) => {
    e.preventDefault();

    if (!canPublish) {
      if (!category) {
        toast.warn("Please select a category for this product");
      } else if (files.filter((f) => !!f.file).length < 3) {
        toast.warn("Please upload at least 3 images");
      } else {
        toast.warn("Please fill in all required fields");
      }
      return;
    }

    const priceNum = Number(price);
    const stockNum = Number(stockQty);

    if (isNaN(priceNum) || priceNum <= 0) {
      return toast.warn("Price must be a positive number.");
    }
    if (isNaN(stockNum) || stockNum < 0) {
      return toast.warn("Stock quantity must be a non-negative number.");
    }

    const payload = {
      name: name.trim(),
      category,                       // Now safely contains a valid ObjectId
      pricePerUnit: priceNum,
      stockQuantity: stockNum,
      description: desc.trim(),
      visibility,
      weight: weight ? Number(weight) : undefined,
      discountPercent: discount ? Number(discount) : undefined,
      productType: productType || undefined,
      images: files.filter((f) => !!f.file).map((f) => f.file),
      sellerName: storeName.trim() || undefined,
      sellerPhone: sellerPhone || undefined,
    };

    try {
      setSubmitLoading(true);
      const created = await api.products.create(payload);

      toast.success("Product created successfully!");
      navigate("/products"); // Redirect to your products list
    } catch (err) {
      console.error("Create product failed:", err);
      const msg =
        err?.data?.message ||
        err?.message ||
        "Failed to create product. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fff8e1]">
      {/* Store Setup Popup */}
      {showStorePopup && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white w-[90%] max-w-md rounded-2xl p-6 shadow-lg text-center">
            <h2 className="text-xl font-bold text-[#5d4037]">Set Up Your Store</h2>
            <p className="mt-2 text-[14px] text-[#5d4037]/70">
              You need to set up your store before you can add products.
            </p>
            <button
              onClick={() => navigate("/store-info")}
              className="mt-6 w-full bg-[#f7c945] text-[#5d4037] font-semibold py-3.5 rounded-2xl active:bg-[#e0b13a]"
            >
              Setup Store Now
            </button>
          </div>
        </div>
      )}

      <div className="app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-16 max-w-2xl mx-auto">
        {/* Top Bar */}
        <div className="flex items-center mt-4 px-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">Add Product</h1>
          <div className="w-9" />
        </div>

        <p className="mt-3 px-4 text-[14px] text-[#5d4037]/80">
          To start selling, all you need is a name, price and at least 3 images.
        </p>

        <form onSubmit={publish} className="mt-6 px-4 space-y-6">
          {/* Product Name */}
          <div>
            <label className="block text-base font-semibold mb-2">Product Name <span className="text-red-500">*</span></label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Fried Rice and Chicken"
              className="w-full h-[52px] bg-white rounded-[14px] border border-[#5d4037] px-4 outline-none focus:border-[#f7c945]"
            />
          </div>

          {/* Category - FIXED */}
          <div>
            <label className="block text-base font-semibold mb-2">Category <span className="text-red-500">*</span></label>
            <div className="relative">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="appearance-none w-full h-[52px] bg-white rounded-[14px] border border-[#5d4037] pl-4 pr-10 text-[#7a6f5c] outline-none focus:border-[#f7c945]"
                disabled={loadingCats}
              >
                <option value="" disabled>
                  {loadingCats ? "Loading categories..." : "Select a category"}
                </option>

                {!loadingCats &&
                  categories.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
              </select>

              <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>
          </div>

          {/* Seller Name & Phone */}
          <div>
            <label className="block text-base font-semibold mb-2">Seller Name (optional)</label>
            <input
              value={sellerName || storeName}
              onChange={(e) => setSellerName(e.target.value)}
              placeholder="e.g. Dee Kitchen"
              className="w-full h-[52px] bg-white rounded-[14px] border border-[#5d4037] px-4 outline-none"
            />
          </div>

          <div>
            <label className="block text-base font-semibold mb-2">Seller Phone (optional)</label>
            <input
              value={sellerPhone}
              onChange={(e) => setSellerPhone(e.target.value)}
              placeholder="0803 123 4567"
              className="w-full h-[52px] bg-white rounded-[14px] border border-[#5d4037] px-4 outline-none"
              type="tel"
            />
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-base font-semibold mb-2">Visibility</label>
            <div className="relative">
              <select
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
                className="appearance-none w-full h-[52px] bg-white rounded-[14px] border border-[#5d4037] pl-4 pr-10 outline-none"
              >
                <option value="local">Local (within my area)</option>
                <option value="global">Global (all customers)</option>
              </select>
              <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-base font-semibold mb-2">Description</label>
            <textarea
              rows={4}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Describe your product..."
              className="w-full bg-white rounded-[14px] border border-[#5d4037] p-4 outline-none"
            />
          </div>

          {/* Pricing & Stock */}
          <div>
            <p className="font-extrabold uppercase text-[13px] tracking-wide mb-3">Pricing & Stock</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Price (₦)</label>
                <div className="relative mt-1">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5d4037]">₦</span>
                  <input
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="0.00"
                    className="w-full h-[52px] bg-white rounded-[14px] border border-[#5d4037] pl-9 outline-none"
                    inputMode="decimal"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Stock Quantity</label>
                <input
                  value={stockQty}
                  onChange={(e) => setStockQty(e.target.value)}
                  placeholder="50"
                  className="w-full h-[52px] bg-white rounded-[14px] border border-[#5d4037] px-4 mt-1 outline-none"
                  inputMode="numeric"
                />
              </div>
            </div>
          </div>

          {/* Media Upload */}
          <div>
            <p className="font-extrabold uppercase text-[13px] tracking-wide mb-2">Product Images</p>
            <p className="text-[13px] text-[#5d4037]/70 mb-3">
              Upload at least <strong>1 image</strong> (max 5)
            </p>

            <button
              type="button"
              onClick={onPick}
              className="w-full h-20 rounded-[14px] border-2 border-dashed border-[#5d4037] bg-[#f3e9bf] text-[#5d4037] font-medium"
            >
              Tap to upload images
            </button>

            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFiles}
              className="hidden"
            />

            {files.length > 0 && (
              <div className="mt-4 space-y-3">
                {files.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-[#eadfb7]">
                    <div className="w-12 h-12 rounded-lg overflow-hidden border">
                      <img src={f.src} alt={f.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{f.name}</p>
                      <p className="text-xs text-gray-500">{f.sizeKB} KB</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(f.id)}
                      className="text-red-500 text-xl leading-none"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Publish Button */}
          <div className="pt-6 flex flex-col items-center">
            <button
              type="submit"
              disabled={!canPublish || submitLoading}
              className={`w-full max-w-[240px] h-12 rounded-full font-semibold text-lg transition-all ${
                !canPublish || submitLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#f7c945] hover:bg-[#f0b800] active:scale-95"
              }`}
            >
              {submitLoading ? "Publishing..." : "Publish Product"}
            </button>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mt-4 text-red-600 underline text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
        <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
          <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
            <div className="w-[120px] h-[6px] rounded-full bg-white" />
          </div>
        </div>
      </div>
    </div>
  );
};